/**
 * One hook for every offline-capable write.
 *
 * Call sites do not branch on connectivity — they call `mutate` and this decides:
 * online, it goes straight out; offline, it is queued and the cache is updated
 * optimistically so the record appears immediately, marked pending.
 *
 * The optimistic row is written into the *persisted* cache, so it survives a reload
 * while still offline. That matters more than it sounds: an agent who queues three
 * notes and then closes the laptop must still see those three notes when it reopens,
 * or they will write them again.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { OFFLINE_OPERATION_SPECS, resolveOfflinePath, type OfflineOperation } from '@ias/shared';

import { http } from '../api/client';
import { useAuth } from '../auth';
import { useI18n } from '../i18n';
import { enqueue } from './outbox';
import { useOffline } from './context';

/** Marker on an optimistic record so the UI can badge it as not-yet-synced. */
export const PENDING_FLAG = '__pendingSync';

export interface OfflineMutationOptions<TInput> {
  operation: OfflineOperation;
  /** Path parameter — the client or claim the write belongs to. */
  targetId?: (input: TInput) => string | undefined;
  /** One-line description shown in the outbox. */
  summary: (input: TInput) => string;
  /**
   * Builds the record to splice into the cache while offline. Returning nothing skips
   * the optimistic update — correct for toggles, where the list already shows the row.
   */
  optimistic?: (input: TInput, entryId: string) => { queryKey: unknown[]; record: unknown } | void;
  /** Applied to a cached list when inserting an optimistic record. */
  onSuccessMessage?: string;
}

export function useOfflineMutation<TInput extends Record<string, unknown>>(
  options: OfflineMutationOptions<TInput>,
) {
  const { isOnline } = useOffline();
  const { user } = useAuth();
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [isPending, setIsPending] = React.useState(false);

  const spec = OFFLINE_OPERATION_SPECS[options.operation];

  const mutate = React.useCallback(
    async (input: TInput): Promise<{ queued: boolean }> => {
      const targetId = options.targetId?.(input);
      setIsPending(true);

      try {
        if (isOnline) {
          const url = resolveOfflinePath(options.operation, targetId);
          await http.request({ url, method: spec.method, data: input });

          for (const root of spec.invalidates) {
            await queryClient.invalidateQueries({ queryKey: [root] });
          }
          if (options.onSuccessMessage) toast.success(t(options.onSuccessMessage));
          return { queued: false };
        }

        if (!user) throw new Error('Not authenticated');

        const entry = await enqueue({
          operation: options.operation,
          targetId,
          payload: input,
          summary: options.summary(input),
          userId: user.id,
        });

        // Show it immediately, flagged, so the queue is visible rather than implied.
        const optimistic = options.optimistic?.(input, entry.id);
        if (optimistic) {
          queryClient.setQueriesData(
            { queryKey: optimistic.queryKey },
            (existing: unknown) => spliceOptimistic(existing, optimistic.record),
          );
        }

        toast.info(t('offline.queued'), { description: options.summary(input) });
        return { queued: true };
      } finally {
        setIsPending(false);
      }
    },
    [isOnline, user, queryClient, options, spec, t],
  );

  return { mutate, isPending, isOnline };
}

/**
 * Inserts a record into whichever shape the cached query holds — a bare array, or the
 * `{ data, meta }` envelope the list endpoints return. Anything else is left untouched
 * rather than guessed at, because a wrong guess corrupts the cache the user is reading.
 */
function spliceOptimistic(existing: unknown, record: unknown): unknown {
  if (Array.isArray(existing)) return [record, ...existing];

  if (existing && typeof existing === 'object' && 'data' in existing) {
    const envelope = existing as { data: unknown; meta?: { total?: number } };
    if (Array.isArray(envelope.data)) {
      return {
        ...envelope,
        data: [record, ...envelope.data],
        meta: envelope.meta ? { ...envelope.meta, total: (envelope.meta.total ?? 0) + 1 } : envelope.meta,
      };
    }
  }

  return existing;
}

/** True when a cached record is an unsynced optimistic insert. */
export function isPendingRecord(record: unknown): boolean {
  return Boolean(record && typeof record === 'object' && PENDING_FLAG in record);
}
